import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { CreateShipmentData, SHIPMENT_STATUSES, JobShipment } from "@/lib/types/job";
import { TrackingNumberInput } from "./TrackingNumberInput";
import { format } from "date-fns";
import { CalendarIcon } from "lucide-react";
import { cn } from "@/lib/utils";

interface AddShipmentDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  deliveryLocations: any[];
  onSave: (data: CreateShipmentData) => Promise<void>;
  editingShipment?: JobShipment | null;
}

const CARRIER_OPTIONS = [
  { value: "FedEx", label: "FedEx" },
  { value: "UPS", label: "UPS" },
  { value: "USPS", label: "USPS" },
  { value: "Local Delivery", label: "Local Delivery" },
  { value: "Other", label: "Other" }
];

export const AddShipmentDialog = ({ 
  open, 
  onOpenChange, 
  deliveryLocations,
  onSave,
  editingShipment 
}: AddShipmentDialogProps) => {
  const [formData, setFormData] = useState<CreateShipmentData>({
    delivery_location_id: "",
    carrier_name: "",
    tracking_numbers: [""],
    shipment_status: "preparing",
    status_notes: "",
  });
  const [selectedCarrier, setSelectedCarrier] = useState<string>("");
  const [customCarrierName, setCustomCarrierName] = useState<string>("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (editingShipment) {
      const carrierName = editingShipment.carrier_name || "";
      const isStandardCarrier = CARRIER_OPTIONS.some(opt => opt.value === carrierName);
      
      setFormData({
        delivery_location_id: editingShipment.delivery_location_id || "",
        carrier_name: carrierName,
        tracking_numbers: editingShipment.tracking_numbers?.length > 0 ? editingShipment.tracking_numbers : [""],
        ship_date: editingShipment.ship_date ? new Date(editingShipment.ship_date) : undefined,
        estimated_delivery_date: editingShipment.estimated_delivery_date ? new Date(editingShipment.estimated_delivery_date) : undefined,
        shipment_status: editingShipment.shipment_status,
        status_notes: editingShipment.status_notes || "",
      });
      
      if (isStandardCarrier) {
        setSelectedCarrier(carrierName);
        setCustomCarrierName("");
      } else {
        setSelectedCarrier("Other");
        setCustomCarrierName(carrierName);
      }
    } else {
      setFormData({
        delivery_location_id: "",
        carrier_name: "",
        tracking_numbers: [""],
        shipment_status: "preparing",
        status_notes: "",
      });
      setSelectedCarrier("");
      setCustomCarrierName("");
    }
  }, [editingShipment, open]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      const filteredNumbers = formData.tracking_numbers.filter(n => n.trim() !== "");
      const carrierName = selectedCarrier === "Other" ? customCarrierName : selectedCarrier;
      
      await onSave({
        ...formData,
        delivery_location_id: formData.delivery_location_id === "other" ? "" : formData.delivery_location_id,
        carrier_name: carrierName,
        tracking_numbers: filteredNumbers,
      });
      onOpenChange(false);
    } catch (error) {
      console.error('Error saving shipment:', error);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{editingShipment ? 'Edit Shipment' : 'Add Shipment'}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="delivery_location">Delivery Location</Label>
            <Select
              value={formData.delivery_location_id}
              onValueChange={(value) => setFormData({ ...formData, delivery_location_id: value })}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select delivery location" />
              </SelectTrigger>
              <SelectContent>
                {deliveryLocations.map((location) => (
                  <SelectItem key={location.id} value={location.id}>
                    {location.delivery_name} - {location.city}, {location.state_province}
                  </SelectItem>
                ))}
                <SelectItem value="other" className="text-muted-foreground italic">
                  Other - Indicate in Shipment Notes
                </SelectItem>
              </SelectContent>
            </Select>
            {formData.delivery_location_id === "other" && (
              <p className="text-sm text-muted-foreground mt-1">
                Please specify the delivery location details in the Shipment Notes field below.
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="carrier">Carrier</Label>
            <Select
              value={selectedCarrier}
              onValueChange={(value) => {
                setSelectedCarrier(value);
                if (value !== "Other") {
                  setCustomCarrierName("");
                }
              }}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select carrier" />
              </SelectTrigger>
              <SelectContent>
                {CARRIER_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {selectedCarrier === "Other" && (
              <Input
                value={customCarrierName}
                onChange={(e) => setCustomCarrierName(e.target.value)}
                placeholder="Enter carrier name"
                className="mt-2"
              />
            )}
          </div>

          <div className="space-y-2">
            <Label>Tracking Numbers</Label>
            <TrackingNumberInput
              trackingNumbers={formData.tracking_numbers}
              onChange={(numbers) => setFormData({ ...formData, tracking_numbers: numbers })}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Ship Date</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "w-full justify-start text-left font-normal",
                      !formData.ship_date && "text-muted-foreground"
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {formData.ship_date ? format(formData.ship_date, "PPP") : "Pick a date"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={formData.ship_date}
                    onSelect={(date) => setFormData({ ...formData, ship_date: date })}
                    initialFocus
                    className="pointer-events-auto"
                  />
                </PopoverContent>
              </Popover>
            </div>

            <div className="space-y-2">
              <Label>Estimated Delivery Date</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "w-full justify-start text-left font-normal",
                      !formData.estimated_delivery_date && "text-muted-foreground"
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {formData.estimated_delivery_date ? format(formData.estimated_delivery_date, "PPP") : "Pick a date"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={formData.estimated_delivery_date}
                    onSelect={(date) => setFormData({ ...formData, estimated_delivery_date: date })}
                    initialFocus
                    className="pointer-events-auto"
                  />
                </PopoverContent>
              </Popover>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="notes">Shipment Notes</Label>
            <Textarea
              id="notes"
              value={formData.status_notes}
              onChange={(e) => setFormData({ ...formData, status_notes: e.target.value })}
              placeholder="Add any additional notes about this shipment"
              rows={3}
            />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={saving}>
              {saving ? 'Saving...' : editingShipment ? 'Update Shipment' : 'Add Shipment'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
